import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';

const ColumnVisibilityModal = ({ 
    show, 
    onHide, 
    columns, 
    visibleColumns, 
    onColumnVisibilityChange,
    pageName 
}) => {
    const [localVisibility, setLocalVisibility] = useState({});
    const prevShowRef = useRef(false);

    // Only initialize local state when modal first opens; don't reset while user is making selections
    useEffect(() => {
        if (show && !prevShowRef.current) {
            const initialVisibility = {};
            columns.forEach(col => {
                initialVisibility[col.key] = visibleColumns[col.key] !== false;
            });
            setLocalVisibility(initialVisibility);
        }
        prevShowRef.current = show;
    }, [show, columns, visibleColumns]);

    const handleRadioChange = (columnKey, isVisible) => {
        setLocalVisibility(prev => ({
            ...prev,
            [columnKey]: isVisible
        }));
    };

    const handleApply = () => {
        // Apply changes to parent component
        onColumnVisibilityChange(localVisibility);
        onHide();
    };

    const handleReset = () => {
        // Reset all columns to visible
        const allVisible = {};
        columns.forEach(col => {
            allVisible[col.key] = true;
        });
        setLocalVisibility(allVisible);
    };

    const visibleCount = Object.values(localVisibility).filter(Boolean).length;
    const canApply = visibleCount >= 1; // At least one column must remain visible

    return (
        <Modal 
            show={show} 
            onHide={onHide} 
            centered
            size="md"
            backdrop="static"
            style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}
            contentClassName="d-flex flex-column"
        >
            <Modal.Header 
                style={{ 
                    backgroundColor: '#f8f9fa', 
                    borderBottom: '2px solid #dee2e6',
                    padding: '15px 20px',
                    flexShrink: 0
                }}
            >
                <Modal.Title 
                    style={{ 
                        fontWeight: 'bold', 
                        fontSize: '1.1rem',
                        color: '#212529'
                    }}
                >
                    SET COLUMNS TO SHOW/HIDE
                </Modal.Title>
                <button
                    type="button"
                    className="btn-close"
                    onClick={onHide}
                    aria-label="Close"
                    style={{ marginLeft: 'auto' }}
                ></button>
            </Modal.Header>
            <Modal.Body style={{ padding: '0', overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
                <div style={{ padding: '15px' }}>
                    {columns.map((column, index) => {
                        // Default to true when undefined so at least one option is checked on first render
                        const isVisible = localVisibility[column.key] !== false;
                        const showId = `col-show-${column.key}-${index}`;
                        const hideId = `col-hide-${column.key}-${index}`;
                        return (
                            <div
                                key={column.key}
                                role="group"
                                aria-label={`${column.label} visibility`}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '12px 15px',
                                    borderBottom: index < columns.length - 1 ? '1px solid #e9ecef' : 'none',
                                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                                    transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#e9ecef';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f8f9fa';
                                }}
                            >
                                <span style={{ fontWeight: '500', color: '#495057', flex: 1 }}>
                                    {column.label}
                                </span>
                                <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexShrink: 0 }}>
                                    <label
                                        htmlFor={showId}
                                        style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', margin: 0, userSelect: 'none' }}
                                    >
                                        <input
                                            id={showId}
                                            type="radio"
                                            name={`column-${column.key}`}
                                            checked={isVisible}
                                            onChange={() => handleRadioChange(column.key, true)}
                                            style={{ marginRight: '6px', cursor: 'pointer' }}
                                        />
                                        <span
                                            style={{ fontSize: '0.9rem', color: '#495057' }}
                                            onClick={() => handleRadioChange(column.key, true)}
                                            role="button"
                                        >
                                            Show
                                        </span>
                                    </label>
                                    <label
                                        htmlFor={hideId}
                                        style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', margin: 0, userSelect: 'none' }}
                                    >
                                        <input
                                            id={hideId}
                                            type="radio"
                                            name={`column-${column.key}`}
                                            checked={!isVisible}
                                            onChange={() => handleRadioChange(column.key, false)}
                                            style={{ marginRight: '6px', cursor: 'pointer' }}
                                        />
                                        <span
                                            style={{ fontSize: '0.9rem', color: '#495057' }}
                                            onClick={() => handleRadioChange(column.key, false)}
                                            role="button"
                                        >
                                            Hide
                                        </span>
                                    </label>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Modal.Body>
            <Modal.Footer style={{ 
                borderTop: '2px solid #dee2e6', 
                padding: '15px 20px',
                backgroundColor: '#f8f9fa',
                flexShrink: 0
            }}>
                {!canApply && (
                    <span style={{ fontSize: '0.875rem', color: '#856404', marginRight: 'auto' }}>
                        At least one column must be visible.
                    </span>
                )}
                <Button 
                    variant="outline-secondary" 
                    onClick={handleReset}
                    style={{ marginRight: 'auto' }}
                >
                    Reset All
                </Button>
                <Button 
                    variant="secondary" 
                    onClick={onHide}
                    style={{ marginRight: '10px' }}
                >
                    Cancel
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleApply}
                    disabled={!canApply}
                >
                    Apply
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ColumnVisibilityModal;
